import { useMemo } from 'react';

import { useI18n } from '../../../../shared/i18n/useI18n.js';
import { useProductStrings } from '../../../../shared/i18n/useProductStrings.js';

import {
	formatProductPrice,
	formatTier,
	formatProductArea,
} from '../../../../shared/utils/index.js';

import {
	parseActiveColumns,
	parseActiveActions,
	makeGetRowState,
	makePlanVisible,
} from '../../utils/unitTableSettings.js';
import {
	DATA_ITEMS_IDS,
	ACTION_IDS,
} from '../../constants/catalogDataDisplay.js';
import { renderActionButtons } from '../ProductActions/renderActionButtons.js';

import { Button } from '../../../../shared/ui/Button/Button.jsx';
import { DataGrid } from '../../../../shared/ui/DataGrid/index.js';
import { HeartButton } from '../../../../shared/ui/HeartButton/HeartButton.jsx';
import { ProductTile } from '../../../../shared/ui/ProductTile/index.js';
import { PriceBlock } from '../../../../shared/ui/PriceBlock/index.js';
import { StatusBadge } from '../../../../shared/ui/StatusBadge/index.js';
import { UnitPlanThumb } from '../../../../shared/ui/UnitPlanThumb/index.js';

import styles from './ProductTiles.module.scss';
import { AVAILABILITY_STATUS } from '../../../../shared/constants/availabilityStatus.js';

// ── Helpers ────────────────────────────────────────────────────────────────────

function getTileCtaLabel(
	getProductsCategoryString,
	product,
	tileState,
	hasSimilarSection,
) {
	const productCategory = product?.product_category ?? null;
	const productId = product?.id_original ?? product?.id_cad;

	if (tileState !== 'inaccessible') {
		return getProductsCategoryString('open', productCategory, {
			id: productId,
		});
	}

	if (hasSimilarSection) {
		return getProductsCategoryString('find_similar', productCategory);
	}

	if (product?.status === 'sold') {
		return getProductsCategoryString('sold', productCategory);
	}

	if (product?.status === 'reserved') {
		return getProductsCategoryString('reserved', productCategory);
	}

	return getProductsCategoryString('inaccessible', productCategory);
}

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * ProductTiles — auto-fill CSS grid of ProductTile cards.
 *
 * Tile data fields are driven by `panelSettings.catalog_list_columns`;
 * only fields with more than one unique value across products are shown.
 * Accessibility (clickability of sold/reserved) follows `unit_sheet_accessibility`.
 *
 * Tile layout:
 * - Header  — HeartButton (right); left slot reserved for future data point
 * - Content — status row (badges + secondary actions) + DataGrid (fields + PriceBlock footer)
 * - Action  — full-width CTA button
 *
 * @param {object}    props
 * @param {Array}     props.filteredProducts              - Product array (pre-sorted/filtered)
 * @param {{ pricesVisible?: boolean, pricesSoldVisible?: boolean, pricesReservedVisible?: boolean }} [props.pricesVisibility]
 * @param {string|null} [props.activeProductKey]  - Active product key (id_cad or uuid)
 * @param {Function} [props.onProductClick]       - (product) => void
 * @param {Function} [props.isFavorite]           - (uuid: string) => boolean
 * @param {Function} [props.toggleFavorite]       - (uuid: string) => void
 * @param {string|null} [props.projectDataUrl]    - Base asset URL; null → thumbnail hidden
 * @param {string|null} [props.logoSrc]           - Pre-resolved logo (from AppContext) for error states
 * @param {boolean}  [props.hasSimilarSection=false] - true → inaccessible units show a "find similar" CTA label
 * @param {boolean}  [props.use3dThumbs=false]   - true → load .png 3D thumbs (TODO: finalize CMS key)
 */
export function ProductTiles({
	activeActions = [],
	activeProductKey = null,
	tileDataItems = [],
	filteredProducts = [],
	logoSrc = null,
	isFavorite,
	isMobile,
	hasSimilarSection = false,
	noDataText,
	onProductClick,
	pricesVisibility = {},
	projectDataUrl = null,
	reservedUnitsAccessible,
	reservedUnitsPlanVisible,
	soldUnitsAccessible,
	soldUnitsPlanVisible,
	tiers = {},
	toggleFavorite,
	use3dThumbs = false, // TODO: finalize panelSettings key (e.g. panelSettings?.unit_thumb_3d === true)
}) {
	// ── i18n ──────────────────────────────────────────────────────────────────
	const { t } = useI18n();
	const getProductsCategoryString = useProductStrings();

	const { pricesVisible } = pricesVisibility;

	const { gridDataItems, hasActionsOrInquiry, orderedActionIds } =
		useMemo(() => {
			const gridItems = tileDataItems.filter(
				(i) =>
					i.id !== DATA_ITEMS_IDS.STATUS &&
					i.id !== DATA_ITEMS_IDS.PRICE &&
					i.id !== DATA_ITEMS_IDS.ACTIONS &&
					i.id !== DATA_ITEMS_IDS.INQUIRY,
			);

			const hasActions = tileDataItems.some(
				(i) =>
					i.id === DATA_ITEMS_IDS.ACTIONS || i.id === DATA_ITEMS_IDS.INQUIRY,
			);

			const orderedIds = tileDataItems.flatMap((item) => {
				if (item.id === DATA_ITEMS_IDS.INQUIRY) return item.id;
				if (item.id === DATA_ITEMS_IDS.ACTIONS)
					return activeActions.map((a) => a.id);
				return [];
			});

			return {
				gridDataItems: gridItems,
				hasActionsOrInquiry: hasActions,
				orderedActionIds: orderedIds,
			};
		}, [tileDataItems, activeActions]);

	const accessibility = {
		reservedUnitsAccessible,
		reservedUnitsPlanVisible,
		soldUnitsAccessible,
		soldUnitsPlanVisible,
	};
	const getTileState = makeGetRowState(accessibility);
	// const getPlanVisible  = makePlanVisible(accessibility)
	const getPlanVisible = (p) =>
		p.status === AVAILABILITY_STATUS.AVAILABLE ||
		(p.status === AVAILABILITY_STATUS.RESERVED && reservedUnitsPlanVisible) ||
		(p.status === AVAILABILITY_STATUS.SOLD && soldUnitsPlanVisible);

	const actionButtonsRenderContext = useMemo(
		() => ({
			buttonClass: '',
			size: 'large',
			variant: 'ghost',
			cta: false,
			isFavorite,
			toggleFavorite,
		}),
		[isFavorite, toggleFavorite],
	);

	return (
		<div className={styles.viewport}>
			{filteredProducts.length === 0 ? (
				<div className={styles.noData}>{noDataText}</div>
			) : (
				<div className={styles.grid}>
					{filteredProducts.map((p) => {
						const key = p.uuid ?? p.id_cad;
						const isActive =
							p.id_cad === activeProductKey || p.uuid === activeProductKey;
						const tileState = getTileState(p);
						const isTileDisabled = tileState === 'inaccessible';

						const { displayPrice, isHidden, isOnOffer, strikethroughPrice } =
							formatProductPrice(p, pricesVisibility);

						const dataItems = gridDataItems.map((item) => ({
							label: t(item.labelKey),
							value: item.getValue(p, { tiers }) ?? '—',
						}));

						const tileClass = [
							isActive ? styles.tileActive : '',
							tileState === 'inaccessible' ? styles.tileInaccessible : '',
							tileState === 'sold' ? styles.tileSold : '',
							tileState === 'reserved' ? styles.tileReserved : '',
						]
							.filter(Boolean)
							.join(' ');

						const handleTileClick = !isTileDisabled
							? () => onProductClick?.(p)
							: undefined;
						const ctaLabel = getTileCtaLabel(
							getProductsCategoryString,
							p,
							tileState,
							hasSimilarSection,
						);

						return (
							<ProductTile
								key={key}
								variant={isMobile ? 'compact' : 'grid'}
								className={tileClass}
								onClick={handleTileClick}
								role="group"
								tabIndex={!isTileDisabled ? 0 : -1}
								aria-label={ctaLabel}
								onKeyDown={
									handleTileClick
										? (e) => {
												if (e.key === 'Enter' || e.key === ' ') {
													e.preventDefault();
													handleTileClick();
												}
											}
										: undefined
								}
							>
								{/* ── Header: future left slot + HeartButton ── */}
								<ProductTile.Header>
									<div className={styles.headerLeft} />
									{hasActionsOrInquiry && (
										<div
											className={styles.cellActions}
											onClick={(e) => e.stopPropagation()}
											onKeyDown={(e) => e.stopPropagation()}
										>
											{renderActionButtons({
												context: {
													...actionButtonsRenderContext,
													onProductClick: handleTileClick,
												},
												product: p,
												orderedActionIds: orderedActionIds,
											})}
										</div>
									)}
								</ProductTile.Header>

								{/* ── Content ── */}
								<ProductTile.Content>
									{/* Floor plan / 3D thumbnail */}
									{projectDataUrl && (
										<UnitPlanThumb
											product={p}
											projectDataUrl={projectDataUrl}
											logoSrc={logoSrc}
											planVisible={getPlanVisible(p)}
											use3d={use3dThumbs}
											className={styles.planThumb}
										/>
									)}

									<div className={styles.productData}>
										{/* Status row: badges (left) + status badge (right) */}
										<div className={styles.statusRow}>
											<div className={styles.statusRowActions}>
												{/* place for product badges */}
											</div>
											<StatusBadge status={p.status} size="small" />
										</div>

										{/* Data grid + price footer */}
										<DataGrid
											columns={isMobile ? 2 : 3}
											size="small"
											items={dataItems}
											className={styles.dataGridRoot}
											gridClassName={styles.dataGrid}
											footer={
												pricesVisible ? (
													<PriceBlock
														displayPrice={displayPrice}
														strikethroughPrice={strikethroughPrice}
														isOnOffer={isOnOffer}
														isHidden={isHidden}
														label={t('catalog.col_price')}
														highlight
													/>
												) : null
											}
										/>
									</div>
								</ProductTile.Content>

								{/* ── Action: CTA ── */}
								<ProductTile.Action>
									<Button
										type="button"
										kind="wide"
										size="large"
										cta
										disabled={isTileDisabled}
										onClick={
											!isTileDisabled
												? (e) => {
														e.stopPropagation();
														onProductClick?.(p);
													}
												: undefined
										}
										onTouchEnd={
											!isTileDisabled ? (e) => e.stopPropagation() : undefined
										}
									>
										{ctaLabel}
									</Button>
								</ProductTile.Action>
							</ProductTile>
						);
					})}
				</div>
			)}
		</div>
	);
}
